import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import LOGO from "../../assets/logo/insightfulTraitsLogo.png";
import AppPopOver from "../popover/component";
import { getCookie } from "../../utils/utility-functions";
import { readCookie } from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import PathHelper from "../../utils/path-helper";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ProgressSteps = ({ currentStep, t }) => {
  const steps = [
    { number: 1, label: `${t("header.status")}`, icon: <CheckIcon /> },
    { number: 2, label: `${t("header.features")}`, icon: <CheckIcon /> },
    { number: 3, label: `${t("header.payment")}`, icon: <CheckIcon /> },
    { number: 4, label: `${t("header.done")}`, icon: <CheckIcon /> },
  ];

  return (
    <div className="hidden lg:flex items-center justify-end w-full max-w-3xl">
      <nav className="flex justify-end items-center space-x-4">
        {steps.map((step, index) => {
          const isFirstStep = step.number === 1;
          const isCompleted = step.number < currentStep;
          const isActive = step.number === currentStep;
          const showCheckmark = isCompleted;

          return (
            <div
              key={step.number}
              className={`flex items-center ${isCompleted ? "opacity-50" : ""}`}
            >
              <div className="flex items-center">
                <div
                  className={`
                    w-6 h-6 rounded-full flex items-center justify-center border-2
                    ${
                      isFirstStep && isActive
                        ? "bg-[#22C55E] border-[#22C55E] text-white"
                        : showCheckmark
                        ? "bg-[#805AED] border-[#805AED] text-white"
                        : isActive
                        ? "bg-[#fffffff] border-[#22C55E] text-[#22C55E]"
                        : "border-gray-200 text-gray-800"
                    }
                  `}
                >
                  {isFirstStep ? (
                    step.icon
                  ) : showCheckmark ? (
                    step.icon
                  ) : (
                    <span
                      className={`text-sm ${
                        isActive ? "font-semibold" : "font-medium"
                      }`}
                    >
                      {step.number}
                    </span>
                  )}
                </div>
                <span
                  className={`ml-2 text-[16px] font-medium
                  ${
                    isFirstStep && isActive
                      ? "text-[#22C55E]"
                      : showCheckmark
                      ? "text-[#805AED]"
                      : isActive
                      ? "text-[#22C55E]"
                      : "text-gray-900"
                  }
                `}
                >
                  {step.label}
                </span>
              </div>
            </div>
          );
        })}
      </nav>
    </div>
  );
};

const Header = ({ funnelStep, personalityImage }) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const token = getCookie("jwtToken");
  const isSignUpPage = pathname.includes("signup");
  const isPlanPage = pathname.includes("plan");
  const isCheckoutPage = pathname.includes("checkout");
  const isConfirmationPage = pathname.includes("confirmation");
  const isResetPasswordPage = pathname.includes("reset-password");
  const isForgotPasswordPage = pathname.includes("forgot-password");
  const isLoginPage = pathname.includes("login");
  const isViewReportUnauth = pathname.includes("view-report");
  const isDashboardPage = pathname.includes("dashboard") && token;

  const isSettingAndViewReportPage =
    pathname.includes("settings") ||
    pathname.includes("career-pre-conten") ||
    pathname.includes("report") ||
    pathname.includes("career-result-view");

  const isAdminPage = pathname.includes("admin");
  const { t } = useTranslation();
  const avatarImage = personalityImage || readCookie("avatarImg");
  const userName = readCookie("uname");
  const accountNumber = readCookie("accnum");
  // const token = localStorage.getItem("jwtToken");
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const dashboardPath = PathHelper(APP_NAVIGATION.DASHBOARD, currentLocale);
  const settingsPath = PathHelper(APP_NAVIGATION.SETTINGS, currentLocale);

  const {
    totalQuestions,
    selectedAnswerCount = 0,
    userGUID: userGuid,
  } = funnelStep || {};

  const hasAssessmentAttemptId = () => {
    const queryParams = new URLSearchParams(search);
    return queryParams.has("assessmentAttemptId");
  };

  const isProgressStepPage = () => {
    // For login and signup pages, only show progress if assessmentAttemptId exists
    if ((isLoginPage || isSignUpPage) && !hasAssessmentAttemptId()) {
      return false;
    }
    return (
      isLoginPage ||
      isSignUpPage ||
      isPlanPage ||
      isCheckoutPage ||
      isConfirmationPage
    );
  };

  const getCurrentStep = () => {
    if (isLoginPage || isSignUpPage) return 1;
    if (isPlanPage) return 2;
    if (isCheckoutPage) return 3;
    if (isConfirmationPage) return 4;
    return 1;
  };

  let progressPercentage =
    selectedAnswerCount > 0 ? (selectedAnswerCount / totalQuestions) * 100 : 0;
  const progress = progressPercentage && progressPercentage.toFixed(0);

  const handleFAQpageRedirect = () => {
    setIsPopoverOpen(false);
    window.open("https://insightfultraits.com/faq/", "_blank");
  };

  const handleSupportPageRedirect = () => {
    setIsPopoverOpen(false);
    window.open("https://insightfultraits.com/contact/", "_blank");
  };

  return (
    <>
      <NavbarContainer
        className={`navbar ${isPlanPage ? "sm:fixed sm:top-0" : "sticky"}`}
      >
        <div className="lg:px-[50px] container max-w-[1260px] mx-auto flex justify-between items-center gap-6">
          <div className="flex flex-col items-center min-w-max">
            <div className="cursor-pointer logo_it">
              <img
                src={LOGO}
                alt="InsightfulTraits Logo"
                width={280}
                height={64}
                className="w-[185px] lg:w-[280px] h-auto flex flex-shrink-0"
                onClick={() => {
                  if (!isPlanPage && !isCheckoutPage) {
                    if (token) {
                      navigate(dashboardPath.next);
                    } else {
                      window.location.href = "https://insightfultraits.com/";
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Progress Bar or Steps */}
          {!isDashboardPage &&
            !isViewReportUnauth &&
            !!userGuid &&
            !isResetPasswordPage &&
            !isForgotPasswordPage &&
            !isAdminPage &&
            !isSettingAndViewReportPage &&
            totalQuestions > 0 &&
            selectedAnswerCount > 0 &&
            !isProgressStepPage() &&
            !isLoginPage && (
              <ProgressBarBackground className="progress">
                <ProgressBarText className="progress_text">
                  {progress} {t("header.progress")}
                </ProgressBarText>
                <ProgressBarForeground
                  className="pro_foreground"
                  style={{ width: `${progress}%` }}
                />
                <BackdropFore className="BackdropFore" />
              </ProgressBarBackground>
            )}

          {/* Progress Steps for specific pages*/}
          {isProgressStepPage() && (
            <ProgressSteps currentStep={getCurrentStep()} t={t} />
          )}

          {/* Dashboard Profile Section */}
          {isDashboardPage && (
            <div className="relative w-fit">
              <div
                className="flex items-center space-x-4 cursor-pointer"
                onClick={() => setIsPopoverOpen(!isPopoverOpen)}
              >
                <div className="flex flex-col items-center w-full">
                  <div className="flex items-center rounded-full cursor-pointer p-1 overflow-hidden">
                    <img
                      src={avatarImage}
                      alt="Avatar"
                      className="object-cover rounded-full transition-transform transform hover:scale-110 w-12 h-12"
                    />
                    <div className="ml-2 cursor-pointer flex lg:hidden">
                      <svg
                        width="29"
                        height="18"
                        viewBox="0 0 29 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1H28"
                          stroke="#444C5E"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M1 9H28"
                          stroke="#444C5E"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M1 17H28"
                          stroke="#444C5E"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <p className="hidden lg:inline text-gray-800 font-bold text-sm sm:text-base">
                {userName}
              </p>

              {isPopoverOpen && (
                <div className="absolute top-full right-0 mt-[-50px] z-50">
                  <AppPopOver
                    isPopoverOpen={isPopoverOpen}
                    setIsPopoverOpen={setIsPopoverOpen}
                    enableDynamicPositioning={false}
                    image={avatarImage}
                    label={userName}
                    accountNo={accountNumber}
                    className="absolute top-12 sm:top-16 right-2 sm:right-0 translate-x-0 sm:translate-x-20 z-50 text-xs text-center font-semibold w-[60vw] sm:w-[256px] max-w-[260px] bg-white"
                    classNameList="text-[14px]"
                    type="Header"
                    content={[
                      {
                        label: `${t("header.subscription")}`,
                        onClick: () => {
                          navigate(`${settingsPath.next}`);
                          setIsPopoverOpen(false);
                        },
                      },
                      {
                        label: isSettingAndViewReportPage
                          ? `${t("header.dashboard")}`
                          : `${t("header.account_settings")}`,
                        onClick: () => {
                          if (isSettingAndViewReportPage) {
                            navigate(`${dashboardPath.next}`);
                            setIsPopoverOpen(false);
                          } else {
                            navigate(`${settingsPath.next}`);
                            setIsPopoverOpen(false);
                          }
                        },
                      },
                      {
                        label: t("header.faqs"),
                        onClick: () => {
                          handleFAQpageRedirect();
                        },
                      },
                      {
                        label: t("header.support"),
                        onClick: () => {
                          handleSupportPageRedirect(false);
                        },
                      },
                    ]}
                    privacyLabel={[
                      {
                        label: `${t("footer.privacy_policy")}`,
                        path: "https://insightfultraits.com/privacy-policy/",
                      },
                      {
                        label: `${t("footer.terms_of_service")}`,
                        path: "https://insightfultraits.com/terms-conditions/",
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </NavbarContainer>
      {isPlanPage && <div className="h-0 md:h-[80px] lg:h-[80px]" />}
    </>
  );
};

export default Header;

const NavbarContainer = styled.nav`
  background-color: var(--bg-white);
  padding: 0.5rem;
  box-shadow: inset 0 0 0 1px var(--shadow-light);
  flex-shrink: 0;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  gap: 10px;
`;

const ProgressBarBackground = styled.div`
  width: 100%;
  max-width: 50rem;
  background-color: var(--bg-light-gray);
  border-radius: 0.5rem;
  overflow: hidden;
  height: 2rem;
  position: relative;
`;

const ProgressBarForeground = styled.div`
  height: 100%;
  background-color: var(--progress-blue);
  transition: width 0.5s ease;
  border-radius: inherit;
`;

const ProgressBarText = styled.div`
  position: absolute;
  top: 0;
  left: 0.5rem;
  height: 100%;
  display: flex;
  align-items: center;
  color: var(--bg-white);
  font-weight: 600;
  pointer-events: none;
  z-index: 1;
  line-height: 1;
`;

const BackdropFore = styled.div`
  display: none;
`;
